import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import './Reports.css';

function Reports() {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // Filter states
  const [period, setPeriod] = useState('monthly');
  const [shipmentType, setShipmentType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        period: showCustomDates ? 'custom' : period,
        type: shipmentType,
      };

      if (showCustomDates && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      const [summaryRes, shipmentsRes] = await Promise.all([
        reportsAPI.getSummary(params),
        reportsAPI.getShipments(params),
      ]);

      setSummary(summaryRes.data.data);
      setShipments(shipmentsRes.data.data.shipments);
    } catch (err) {
      setError('Failed to load report data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    fetchReportData();
  };

  const handleExport = async (format) => {
    try {
      setMessage({ type: '', text: '' });

      const params = {
        period: showCustomDates ? 'custom' : period,
        type: shipmentType,
      };

      if (showCustomDates && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      let response;
      let filename;

      if (format === 'excel') {
        response = await reportsAPI.exportExcel(params);
        filename = `shipments_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      } else if (format === 'pdf') {
        response = await reportsAPI.exportPDF(params);
        filename = `shipments_report_${new Date().toISOString().split('T')[0]}.pdf`;
      } else {
        response = await reportsAPI.exportCSV(params);
        filename = `shipments_report_${new Date().toISOString().split('T')[0]}.csv`;
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: `Report exported successfully as ${format.toUpperCase()}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to export report' });
      console.error(err);
    }
  };

  const handlePeriodChange = (e) => {
    const value = e.target.value;
    setPeriod(value);
    setShowCustomDates(value === 'custom');
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="admin" />

      <div className="content">
        <h1 className="page-title">Shipment Reports</h1>

        {error && <div className="error-message">{error}</div>}
        {message.text && (
          <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
            {message.text}
          </div>
        )}

        {/* Filters Section */}
        <div className="reports-filters">
          <div className="filter-group">
            <label>Time Period:</label>
            <select value={period} onChange={handlePeriodChange}>
              <option value="daily">Last 24 Hours</option>
              <option value="weekly">Last 7 Days</option>
              <option value="monthly">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {showCustomDates && (
            <>
              <div className="filter-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="filter-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </>
          )}

          <div className="filter-group">
            <label>Shipment Type:</label>
            <select value={shipmentType} onChange={(e) => setShipmentType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="domestic">Domestic Only</option>
              <option value="international">International Only</option>
            </select>
          </div>

          <button className="btn-generate" onClick={handleGenerateReport} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="summary-section">
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-icon">📦</div>
                <div className="card-content">
                  <div className="card-value">{summary.total_shipments}</div>
                  <div className="card-label">Total Shipments</div>
                </div>
              </div>

              <div className="summary-card">
                <div className="card-icon">🏠</div>
                <div className="card-content">
                  <div className="card-value">{summary.by_type?.domestic || 0}</div>
                  <div className="card-label">Domestic</div>
                </div>
              </div>

              <div className="summary-card">
                <div className="card-icon">🌍</div>
                <div className="card-content">
                  <div className="card-value">{summary.by_type?.international || 0}</div>
                  <div className="card-label">International</div>
                </div>
              </div>

              <div className="summary-card">
                <div className="card-icon">📅</div>
                <div className="card-content">
                  <div className="card-value">{summary.start_date} to {summary.end_date}</div>
                  <div className="card-label">Date Range</div>
                </div>
              </div>
            </div>

            {/* Top Users */}
            {summary.top_users && summary.top_users.length > 0 && (
              <div className="top-section">
                <h3>Top Users</h3>
                <div className="top-list">
                  {summary.top_users.map((user, index) => (
                    <div key={index} className="top-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="name">{user.name}</span>
                      <span className="count">{user.count} shipments</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Destinations */}
            {summary.top_destinations && summary.top_destinations.length > 0 && (
              <div className="top-section">
                <h3>Top Destinations</h3>
                <div className="top-list">
                  {summary.top_destinations.map((dest, index) => (
                    <div key={index} className="top-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="name">{dest.city}, {dest.state}</span>
                      <span className="count">{dest.count} shipments</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Export Buttons */}
        <div className="export-section">
          <h3>Export Report</h3>
          <div className="export-buttons">
            <button className="btn-export excel" onClick={() => handleExport('excel')}>
              📊 Export to Excel
            </button>
            <button className="btn-export csv" onClick={() => handleExport('csv')}>
              📄 Export to CSV
            </button>
            <button className="btn-export pdf" onClick={() => handleExport('pdf')}>
              📑 Export to PDF
            </button>
          </div>
        </div>

        {/* Shipments Table */}
        {shipments.length > 0 && (
          <div className="shipments-preview">
            <h3>Shipments Preview ({shipments.length} total)</h3>
            <div className="table-container">
              <table className="shipments-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Created</th>
                    <th>Created By</th>
                    <th>Shipper</th>
                    <th>Consignee</th>
                    <th>Destination</th>
                    <th>Service</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.slice(0, 10).map((shipment) => (
                    <tr key={shipment.id}>
                      <td>{shipment.id}</td>
                      <td>
                        <span className={`badge-type ${shipment.shipment_type}`}>
                          {shipment.shipment_type}
                        </span>
                      </td>
                      <td>{new Date(shipment.created_at).toLocaleDateString()}</td>
                      <td>{shipment.created_by}</td>
                      <td>{shipment.shipper_name}</td>
                      <td>{shipment.consignee_name}</td>
                      <td>{shipment.consignee_city}, {shipment.consignee_state}</td>
                      <td>{shipment.service}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shipments.length > 10 && (
                <div className="table-note">
                  Showing first 10 of {shipments.length} shipments. Export to see all data.
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && shipments.length === 0 && summary && (
          <div className="no-data">
            <p>No shipments found for the selected period.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
